/**
 * React application entry point.
 *
 * Initialises:
 * - i18next with nl/en translations (NFR-023)
 * - TanStack Query client with default stale time
 * - React Router for SPA navigation
 * - Auth session check on startup
 */
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import App from "./App";
import { Toaster } from "./components/ui/sonner";
import nl from "./i18n/nl.json";
import en from "./i18n/en.json";
import "./index.css";

// Initialise i18next (NFR-023)
i18n.use(initReactI18next).init({
  resources: {
    nl: { translation: nl },
    en: { translation: en },
  },
  lng: "nl", // Default to Dutch
  fallbackLng: "nl",
  interpolation: {
    escapeValue: false, // React already escapes
  },
});

// TanStack Query client
// - staleTime: 30 seconds (matches dashboard refresh interval)
// - retry: 1 time on failure
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <App />
        {/* Global toast notifications — positioned top-right by default */}
        <Toaster richColors closeButton />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
